import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Box, Button, HStack, Text, VStack, Icon, IconButton } from '@chakra-ui/react'
import { MdLightbulb, MdClose } from 'react-icons/md'
import { useTranslation } from 'react-i18next'

export type TourStep = {
  key: string
  /** CSS selector of the element to spotlight (e.g. '[data-tour="zoom-tools"]'). */
  target: string
  title: string
  body: string
  /** Optional action list rendered as bullet points below the body. */
  bullets?: string[]
  /** Preferred side for the tooltip relative to the target. */
  placement?: 'top' | 'bottom' | 'left' | 'right'
  /** Extra px around the target in the spotlight cut-out. */
  padding?: number
}

type Rect = { top: number; left: number; width: number; height: number }

const TOOLTIP_W = 320

/**
 * Lightweight, dependency-free product tour: a dimmed overlay with a spotlight
 * cut-out over the current step's target + a tooltip with prev/next/skip. It
 * polls for the target each frame so steps that live inside a modal (which
 * mounts later) light up as soon as they appear, and follows layout/scroll.
 */
export function GuidedTour({
  steps,
  stepIndex,
  isOpen,
  onNext,
  onPrev,
  onClose,
}: {
  steps: TourStep[]
  stepIndex: number
  isOpen: boolean
  onNext: () => void
  onPrev: () => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [rect, setRect] = useState<Rect | null>(null)
  const rafRef = useRef<number | undefined>(undefined)
  const step = isOpen ? steps[stepIndex] : undefined

  // Track the target's viewport rect every frame while the step is active.
  useEffect(() => {
    if (!step) {
      setRect(null)
      return
    }
    let cancelled = false
    // Drop the previous step's rect immediately: without this, a new target that
    // isn't measurable yet would leave the spotlight sitting on the OLD target's
    // position — a highlight pointing at the wrong thing, which is worse than none.
    setRect(null)
    const tick = () => {
      if (cancelled) return
      const el = document.querySelector(step.target) as HTMLElement | null
      if (el) {
        const r = el.getBoundingClientRect()
        // Ignore zero-size (not laid out yet) — keep waiting rather than
        // spotlighting a collapsed element.
        if (r.width > 0 && r.height > 0) {
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
        }
      } else {
        setRect(null)
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    tick()
    return () => {
      cancelled = true
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [step])

  // Bring the target into view when the step changes.
  //
  // A step change often lands while the layout is still settling: a modal closing
  // (Chakra LOCKS body scroll while one is open, so an immediate scrollIntoView is
  // a silent no-op), or a panel unfolding. So instead of scrolling once — or
  // firing several smooth scrolls that interrupt each other — poll until the
  // target is actually within the viewport, then stop.
  useEffect(() => {
    if (!step) return
    let cancelled = false
    let attempts = 0
    const MARGIN = 90 // keep the target clear of the viewport edges

    const ensureVisible = () => {
      if (cancelled) return
      const el = document.querySelector(step.target) as HTMLElement | null
      if (el) {
        const r = el.getBoundingClientRect()
        const measurable = r.width > 0 && r.height > 0
        const inView = r.top >= MARGIN && r.bottom <= window.innerHeight - MARGIN
        if (measurable && inView) return // settled — nothing left to do
        if (measurable) {
          // First pass glides; later passes jump, so a still-animating smooth
          // scroll can't swallow the correction and leave us short of the target.
          el.scrollIntoView({
            block: 'center',
            inline: 'center',
            behavior: attempts === 0 ? 'smooth' : 'auto',
          })
        }
      }
      if (++attempts < 14) setTimeout(ensureVisible, 120)
    }

    ensureVisible()
    return () => { cancelled = true }
  }, [step])

  if (!step) return null

  const pad = step.padding ?? 8
  const spot = rect
    ? {
        top: rect.top - pad,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
      }
    : null

  const vw = window.innerWidth
  const vh = window.innerHeight

  // Tooltip position: place on the preferred side of the spot, clamped to viewport.
  let tipTop = vh / 2 - 80
  let tipLeft = vw / 2 - TOOLTIP_W / 2
  if (spot) {
    const gap = 14
    const place = step.placement ?? 'bottom'
    const belowRoom = vh - (spot.top + spot.height)
    const aboveRoom = spot.top
    if (place === 'left' && spot.left > TOOLTIP_W + gap) {
      tipLeft = spot.left - TOOLTIP_W - gap
      tipTop = spot.top
    } else if (place === 'right' && vw - (spot.left + spot.width) > TOOLTIP_W + gap) {
      tipLeft = spot.left + spot.width + gap
      tipTop = spot.top
    } else if (place === 'top' && aboveRoom > 290) {
      tipLeft = spot.left + spot.width / 2 - TOOLTIP_W / 2
      tipTop = spot.top - gap - 280
    } else {
      // default bottom, flip above if not enough room
      tipLeft = spot.left + spot.width / 2 - TOOLTIP_W / 2
      tipTop = belowRoom > 290 || belowRoom >= aboveRoom ? spot.top + spot.height + gap : spot.top - gap - 280
    }
    tipLeft = Math.max(12, Math.min(tipLeft, vw - TOOLTIP_W - 12))
    tipTop = Math.max(12, Math.min(tipTop, vh - 320))
  }

  const isLast = stepIndex === steps.length - 1

  return createPortal(
    <Box position="fixed" inset={0} zIndex={2400} pointerEvents="none">
      {/* Dim + spotlight. The huge box-shadow darkens everything except the hole;
          pointerEvents stay off so the user can still click the target through it. */}
      {spot ? (
        <Box
          position="fixed"
          top={`${spot.top}px`}
          left={`${spot.left}px`}
          width={`${spot.width}px`}
          height={`${spot.height}px`}
          borderRadius="12px"
          border="2px solid"
          borderColor="teal.300"
          boxShadow="0 0 0 9999px rgba(15, 23, 42, 0.6)"
          transition="top 0.15s ease, left 0.15s ease, width 0.15s ease, height 0.15s ease"
          pointerEvents="none"
        />
      ) : (
        <Box position="fixed" inset={0} bg="rgba(15, 23, 42, 0.6)" pointerEvents="none" />
      )}

      {/* Tooltip card — matches the onboarding tour's coachmark style. */}
      <Box
        position="fixed"
        top={`${tipTop}px`}
        left={`${tipLeft}px`}
        w={`${TOOLTIP_W}px`}
        maxW="calc(100vw - 24px)"
        maxH="calc(100vh - 24px)"
        bg="white"
        borderRadius="2xl"
        boxShadow="0 24px 64px -12px rgba(15, 23, 42, 0.45), 0 0 0 1px rgba(15,23,42,0.08)"
        overflowY="auto"
        overflowX="hidden"
        transition="all 0.3s cubic-bezier(0.22, 1, 0.36, 1)"
        pointerEvents="auto"
      >
        {/* Header strip — gradient teal→cyan with pastille step number */}
        <Box bgGradient="linear(135deg, teal.600 0%, teal.500 50%, cyan.400 100%)" px={4} py={3} position="relative">
          <Box
            position="absolute" top={0} left={0} right={0} bottom={0}
            background="radial-gradient(circle at 80% 20%, rgba(255,255,255,0.25) 0%, transparent 55%)"
            pointerEvents="none"
          />
          <HStack justify="space-between" align="center" position="relative" zIndex={1}>
            <HStack spacing={3}>
              <Box
                w="34px" h="34px"
                borderRadius="full"
                bg="whiteAlpha.300"
                border="1px solid"
                borderColor="whiteAlpha.500"
                color="white"
                display="grid"
                sx={{ placeItems: 'center' }}
                fontWeight="800"
                fontSize="sm"
                backdropFilter="blur(8px)"
              >
                {stepIndex + 1}
              </Box>
              <Text fontSize="xs" color="whiteAlpha.900" fontWeight="600" letterSpacing="wider" textTransform="uppercase">
                Étape {stepIndex + 1} / {steps.length}
              </Text>
            </HStack>
            <IconButton
              aria-label={t('blotAnalysis.tour.close')}
              icon={<MdClose />}
              size="xs"
              variant="ghost"
              color="white"
              _hover={{ bg: 'whiteAlpha.300' }}
              onClick={onClose}
            />
          </HStack>
        </Box>

        {/* Body */}
        <VStack align="stretch" spacing={3} px={5} py={4}>
          <HStack spacing={3} align="start">
            <Box
              w="40px" h="40px" minW="40px"
              borderRadius="xl"
              bgGradient="linear(135deg, teal.50, cyan.50)"
              border="1px solid"
              borderColor="teal.100"
              display="grid"
              sx={{ placeItems: 'center' }}
              color="teal.600"
            >
              <Icon as={MdLightbulb} boxSize={5} />
            </Box>
            <Text fontSize="sm" fontWeight="bold" color="gray.900" lineHeight="1.3" pt={0.5}>
              {step.title}
            </Text>
          </HStack>

          <Text fontSize="sm" color="gray.600" lineHeight="1.5">
            {step.body}
          </Text>
          {step.bullets && step.bullets.length > 0 && (
            <VStack align="stretch" spacing={1.5} pl={0.5}>
              {step.bullets.map((b, i) => (
                <HStack key={i} align="start" spacing={2}>
                  <Box as="span" mt="7px" w="5px" h="5px" borderRadius="full" bg="teal.400" flexShrink={0} />
                  <Text fontSize="sm" color="gray.600" lineHeight="1.5">
                    {b}
                  </Text>
                </HStack>
              ))}
            </VStack>
          )}
          {!rect && (
            <Text fontSize="xs" color="orange.500" fontStyle="italic">
              En attente de l'élément…
            </Text>
          )}

          {/* progress dots */}
          <HStack spacing={1.5} pt={1} justify="center">
            {steps.map((_, i) => (
              <Box
                key={i}
                w={i === stepIndex ? '20px' : '6px'}
                h="6px"
                borderRadius="full"
                bg={i === stepIndex ? 'teal.500' : i < stepIndex ? 'teal.200' : 'gray.200'}
                transition="all 0.25s ease"
              />
            ))}
          </HStack>
        </VStack>

        {/* Footer actions */}
        <HStack justify="space-between" px={5} py={3} bg="gray.50" borderTop="1px solid" borderColor="gray.100">
          <Button variant="ghost" size="sm" color="gray.500" onClick={onClose}>
            Passer
          </Button>
          <HStack spacing={2}>
            {stepIndex > 0 && (
              <Button size="sm" variant="outline" onClick={onPrev} colorScheme="gray">
                Précédent
              </Button>
            )}
            <Button
              size="sm"
              colorScheme="teal"
              onClick={isLast ? onClose : onNext}
              bgGradient="linear(135deg, teal.500, cyan.500)"
              _hover={{ bgGradient: 'linear(135deg, teal.600, cyan.600)' }}
            >
              {isLast ? 'Terminer' : 'Suivant'}
            </Button>
          </HStack>
        </HStack>
      </Box>
    </Box>,
    document.body,
  )
}
